import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { WindowToken } from '@app/shared/window.token';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { TargetService } from '@app/target/services/target.service';
import { SearchUrlService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'dgx-skill-signal-more-button',
  templateUrl: './skill-signal-more-button.component.html',
  styleUrls: ['./skill-signal-more-button.component.scss'],
})
export class SkillSignalMoreButton
  extends SubscriberBaseDirective
  implements OnInit
{
  @Input() public isLoggedIn: boolean;
  @Input() public ownerIsViewing: boolean;
  @Input() public tag: TagsApi.Tag;

  @Output() public onModalMenu = new EventEmitter<Event>();

  public isFocusSkill: boolean;

  public i18n = this.translateService.instant([
    'Core_ReportAProblem',
    'Core_RemoveSkill',
    'Core_FindContent',
    'Core_AddToJobRole',
    'dgProfileOverview_RemoveSkill',
    'dgProfileOverview_FindExperts',
    'dgTagRating_AddFocusSkill',
    'dgTagRating_RemoveFocusSkill',
    'ProfileCtrl_ExperienceReported',
    'dgProfileOverview_AddSkillOrTopic',
    'dgProfileOverview_AddSkill',
  ]);

  public menuConfig: MenuViewModel[] = [
    {
      title: this.i18n.Core_FindContent,
      defaultAction: () => {
        const url = this.searchUrlService.getGlobalSearchURL(this.tag.name);
        this.performSearch(url);
      },
      isHidden: () => !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.dgProfileOverview_FindExperts,
      defaultAction: () => {
        const url = this.searchUrlService.getPeopleSearchURL(this.tag.name);
        this.performSearch(url);
      },
      isHidden: () => !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.dgTagRating_AddFocusSkill,
      defaultAction: (event) => this.toggleFocusSkill(),
      isHidden: () =>
        !this.ownerIsViewing || this.isFocusSkill || !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.dgTagRating_RemoveFocusSkill,
      defaultAction: (event) => this.toggleFocusSkill(),
      isHidden: () =>
        !this.ownerIsViewing || !this.isFocusSkill || !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_RemoveSkill,
      defaultAction: (event) => this.removeSkill(),
      isHidden: () => !this.ownerIsViewing || !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.dgProfileOverview_AddSkillOrTopic,
      defaultAction: (event) => this.addSkill(),
      isHidden: () => this.ownerIsViewing || !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_AddToJobRole, // yes this really is the add to plan i18n string
      defaultAction: (event) => this.addToPlan(),
      isHidden: () => !this.ownerIsViewing || !this.isLoggedIn,
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_ReportAProblem,
      defaultAction: (event) => this.showReportModal(event),
      isHidden: () => false,
      preventRefocus: true,
    },
  ];

  constructor(
    private translateService: TranslateService,
    public authService: AuthService,
    private notifierService: NotifierService,
    private tagsService: TagsService,
    private targetService: TargetService,
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private elementRef: ElementRef,
    private searchUrlService: SearchUrlService,
    @Inject(WindowToken) private windowRef: Window
  ) {
    super();
  }

  public ngOnInit(): void {
    this.tagsService.userTagsModified
      .pipe(this.takeUntilDestroyed())
      .subscribe((tag: TagsApi.TagDetails) => {
        if (tag) {
          this.tag = tag;
          this.isFocusSkill = tag.requestingUserIsFocused;
        }
      });
    this.isFocusSkill = this.tag.requestingUserIsFocused;
  }

  public addToPlan(): void {
    this.targetService
      .showAddToTargetModal('JobRole', this.tag, this.elementRef.nativeElement)
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }

  public performSearch(url: string): void {
    this.windowRef.open(this.windowRef.location.origin + url);
  }

  public toggleFocusSkill(): void {
    this.tagsService
      .toggleUserInterestFocus(this.tag as TagsApi.TagDetails)
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }

  public addSkill(): void {
    this.tagsService
      .addUserTag(this.tag)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.notifierService.showSuccess(
          this.translateService.instant('dgProfileOverview_AddSkill', {
            skillName: this.tag.title,
          })
        );
      });
  }

  public removeSkill(): void {
    this.tagsService
      .removeUserTag(this.tag)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.notifierService.showSuccess(
          this.translateService.instant('dgProfileOverview_RemoveSkill', {
            skillName: this.tag.title,
          })
        );
        const userUrl = this.authService?.authUser.viewerProfile.vanityUrl;
        let url: string;
        url = `/profile/${userUrl}/`;
        window.location.href = `${window.location.origin}${url}overview`;
      });
  }

  public showReportModal(event): Subscription {
    this.onModalMenu.emit();
    return this.profileOverviewActionsService
      .showReportModal(
        event,
        this.tag.tagId,
        this.tag.resourceType,
        this.elementRef.nativeElement
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.notifierService.showSuccess(
          this.i18n.ProfileCtrl_ExperienceReported
        );
      });
  }
}
